import { DatePicker } from "antd";
import fr_FR from "antd/lib/date-picker/locale/fr_FR";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import { Form, Formik } from "formik";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { ReactComponent as NextIcon } from "../../../../assets/img/icons/next.svg";
import { ReactComponent as PrevIcon } from "../../../../assets/img/icons/prev.svg";
import { exportMonthEndBalanceSheet } from "../../../../data/slices/exports";
import { closeModal } from "../../../../data/slices/modals";
import { parseDate } from "../../../../utilities/filters";
import { formatFiscalYearEndLabel } from "../../../../utilities/periodPresets";
import Button from "../../../Button";
import Overlay from "../../../Overlay";
import HeaderOverlay from "../../../Project/Header";
import Title from "../../../Project/Title";
import "./index.scss";

function ExportMonthEndBilan({ open, onCancel, data }) {
  const dispatch = useDispatch();
  const { selectedMonths } = useSelector((state) => state.layout);
  const { exportStatus } = useSelector((state) => state.exports);
  const [pickerOpen, setPickerOpen] = useState(false);

  const ExportSchema = Yup.object().shape({
    month: Yup.mixed().required("Veuillez sélectionner un mois"),
  });

  const defaultMonth = useMemo(() => {
    if (selectedMonths?.length) {
      const last = selectedMonths[selectedMonths.length - 1];
      const parsed = parseDate(last);
      if (parsed?.isValid?.()) {
        return parsed.startOf("month");
      }
    }
    return dayjs().startOf("month");
  }, [selectedMonths]);

  const initialValues = useMemo(
    () => ({
      month: defaultMonth,
    }),
    [defaultMonth]
  );

  const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    setPickerOpen(false);

    try {
      const month = dayjs(values.month).format("YYYY-MM");
      if (!data?.projectId || !month || month === "Invalid Date") {
        return;
      }

      const result = await dispatch(
        exportMonthEndBalanceSheet({
          projectId: data.projectId,
          month,
        })
      );

      if (result.meta.requestStatus === "fulfilled") {
        resetForm({ values: initialValues });
        dispatch(closeModal("export-month-end-bilan-modal"));
      }
    } catch (error) {
      console.error("Export bilan error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = (resetForm) => {
    if (resetForm) {
      resetForm({ values: initialValues });
    }
    setPickerOpen(false);

    if (typeof onCancel === "function") {
      onCancel();
    } else {
      dispatch(closeModal("export-month-end-bilan-modal"));
    }
  };

  const isExporting = exportStatus === "loading";

  return (
    <Overlay show={open} closable={true}>
      <HeaderOverlay showlogo>
        <Title isDelete subTitle={`Exporter un bilan`} />

        <Formik
          initialValues={initialValues}
          validationSchema={ExportSchema}
          onSubmit={handleFormSubmit}
          validateOnChange={true}
          validateOnBlur={true}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            handleSubmit,
            resetForm,
          }) => {
            const monthValue = values.month ? dayjs(values.month) : null;
            const monthButtonLabel = monthValue?.isValid()
              ? formatFiscalYearEndLabel(monthValue.format("YYYY-MM"))
              : "à définir";
            const busy = isSubmitting || isExporting;

            return (
              <Form
                className="export-month-end-bilan-form"
                onSubmit={handleSubmit}
              >
                <div className="export-month-end-bilan-content">
                  <div className="export-month-end-bilan-content-wrapper">
                    <div className="export-month-end-bilan-section">
                      <div className="section-wrapper-header">
                        <div className="section-title">
                          Sélectionner le mois de clôture du bilan à exporter:
                        </div>
                      </div>
                      <div className="section-content">
                        <div className="period-affichee-picker-slot period-affichee-fiscal-slot export-month-end-bilan-picker-slot">
                          <button
                            type="button"
                            className="period-affichee-current-btn period-affichee-exercice-btn"
                            onClick={() => setPickerOpen((prev) => !prev)}
                            disabled={busy}
                          >
                            {monthButtonLabel}
                          </button>
                          <DatePicker
                            open={pickerOpen}
                            onOpenChange={setPickerOpen}
                            value={monthValue}
                            onChange={(date) => {
                              setFieldValue("month", date);
                              setPickerOpen(false);
                            }}
                            picker="month"
                            locale={fr_FR}
                            format="MMM YYYY"
                            allowClear={false}
                            disabled={busy}
                            className="period-affichee-hidden-picker"
                            dropdownClassName="period-affichee-fiscal-dropdown"
                            panelRender={(panel) => panel}
                            placement="bottomLeft"
                            getPopupContainer={() => document.body}
                            superNextIcon={<NextIcon />}
                            superPrevIcon={<PrevIcon />}
                          />
                        </div>
                        {errors.month && touched.month && (
                          <div className="error-message">{errors.month}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="verify-inputs-popup">
                    <Button
                      text={"Annuler"}
                      onClick={() => handleCancel(resetForm)}
                      className={"action-button"}
                      type="button"
                      disabled={busy}
                    />
                    <Button
                      text={"Continuer"}
                      htmlType="submit"
                      className={"action-button agree-action-button"}
                      disabled={busy || !values.month}
                      loading={busy}
                    />
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </HeaderOverlay>
    </Overlay>
  );
}

export default ExportMonthEndBilan;
